// Company status constants
export const COMPANY_STATUS = {
    PENDING: 'PENDING',
    EVALUATION: 'EVALUATION',
    APPROVED: 'APPROVED',
    RECEIVED: 'RECEIVED',
    ASSIGNED: 'ASSIGNED',
    INTEGRATION_REQUESTED: 'INTEGRATION_REQUESTED',
    INTEGRATION_EXPIRED: 'INTEGRATION_EXPIRED'
};

// Roles that require company status checking
export const RESTRICTED_ROLES = ['ROLE_BENEFICIARY', 'ROLE_CONFIDI'];

// Routes that are always allowed regardless of company status
// Note: /primo-accesso is NOT here - it's only for users with 0 companies
export const ALWAYS_ALLOWED_ROUTES = [
    '/documenti',
    '/profilo-aziendale',
    '/profilo',
    '/imieimoduli',
    '/richieste-integrazioni',
    '/login',
    '/registration',
    '/reset-password'
];

// Map company status to additional allowed routes
// null means all routes are allowed
export const STATUS_ROUTE_MAP = {
    // Primo accesso: azienda appena creata, nessun documento. Solo le rotte sempre consentite.
    [COMPANY_STATUS.PENDING]: [],
    // Stati in cui il fornitore opera normalmente sulla propria qualifica (documenti + questionario ESG):
    // deve poter raggiungere /domande/:id/ per compilare e, soprattutto, per rispondere alle integrazioni.
    [COMPANY_STATUS.EVALUATION]: null,
    [COMPANY_STATUS.APPROVED]: null,
    [COMPANY_STATUS.RECEIVED]: null,
    [COMPANY_STATUS.ASSIGNED]: null,
    [COMPANY_STATUS.INTEGRATION_REQUESTED]: null,
    [COMPANY_STATUS.INTEGRATION_EXPIRED]: null
};

// Default redirect when route is not allowed
export const DEFAULT_REDIRECT = '/documenti';

/**
 * Check if a route is allowed based on company status and role
 * @param {string} pathname - The route pathname to check
 * @param {string|null} status - The company status ('pending', 'evaluation', 'active', or null)
 * @param {string} role - The user's role
 * @param {boolean} hasCompanies - Whether the user has any companies
 * @returns {boolean} - True if route is allowed, false otherwise
 */
export const isRouteAllowed = (pathname, status, role, hasCompanies) => {
    // Other roles (not ROLE_BENEFICIARY/ROLE_CONFIDI) have no company restrictions
    if (!RESTRICTED_ROLES.includes(role)) {
        return true;
    }

    // Special handling for /primo-accesso - only allowed when user has NO companies
    if (pathname === '/primo-accesso') {
        return !hasCompanies; // Only allow if user has no companies
    }

    // Rotte sempre consentite (documenti, i miei moduli, profilo, auth...): devono essere
    // raggiungibili anche quando la lista aziende non e' ancora stata caricata — es. apertura
    // diretta di un link (il "Modulo di Valutazione ESG") o ricarica pagina. Altrimenti la
    // guardia, vedendo hasCompanies=false durante il caricamento, sbatte l'utente fuori.
    if (ALWAYS_ALLOWED_ROUTES.some(route => pathname === route || pathname.startsWith(route + '/'))) {
        return true;
    }

    // No companies at all → only allow auth routes (not /primo-accesso, handled above)
    if (!hasCompanies) {
        return ['/login', '/registration', '/reset-password'].includes(pathname);
    }

    // Company exists but no status → treat as 'pending'
    const effectiveStatus = status || COMPANY_STATUS.PENDING;

    // Debug: log status values to help identify format issues
    if (status && !STATUS_ROUTE_MAP.hasOwnProperty(status)) {
        console.warn('Unknown company status value:', status, 'treating as pending');
    }

    // Check status-specific routes
    const allowedRoutes = STATUS_ROUTE_MAP[effectiveStatus];

    // null means all routes are allowed (active status)
    if (allowedRoutes === null) {
        return true;
    }

    // If allowedRoutes is undefined (unknown status), treat as pending
    if (allowedRoutes === undefined) {
        return false;
    }

    // Check if pathname is in the allowed routes for this status
    return allowedRoutes.includes(pathname);
};

/**
 * Check if a menu item should be visible based on route access rules
 * @param {string|null} href - The menu item's href (route path)
 * @param {string|null} status - The company status
 * @param {string} role - The user's role
 * @param {boolean} hasCompanies - Whether the user has any companies
 * @returns {boolean} - True if menu item should be visible, false otherwise
 */
export const isMenuItemAllowed = (href, status, role, hasCompanies) => {
    // Items without href (like section headers) are always shown
    if (!href) {
        return true;
    }

    return isRouteAllowed(href, status, role, hasCompanies);
};
